import { ASSET_STATUS_LABELS, ASSET_STATUS_TONES, ASSET_TYPE_SUBTITLES } from '@fos/shared/labels';
import { Plus, Server } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { ASSET_TYPE_ICONS, dateText, renewalTone, TONE_TEXT } from '../maintenance-format';
import { AddAssetDialog } from './AddAssetDialog';

/**
 * Sekcja „Na naszym serwerze” w przeglądzie profilu klienta (Figma 05 Profil REA, 3026:2544):
 * nagłówek z linkiem „Utrzymanie” (lista zasobów z filtrem klienta) i wiersze 44 px: ikona typu, nazwa,
 * „strona · Zenbox”, „odnowienie 15.03.2027” i status (bez neutralnego „Aktywna”, jak w Figmie).
 * Bez modułu Utrzymanie sekcja się nie pokazuje. `title={null}` = same wiersze (np. w zakładce Utrzymanie).
 */
export function ClientAssetsSection({
  clientId,
  title = 'Na naszym serwerze',
  className,
}: {
  clientId: string;
  title?: string | null;
  className?: string;
}) {
  const access = useAccess();
  const navigate = useNavigate();
  const enabled = access.can('maintenance');
  const canAdd = access.can('maintenance', 'edit');
  const query = trpc.maintenance.listAssets.useQuery({ clientId }, { enabled });
  const [adding, setAdding] = useState(false);

  if (!enabled) return null;

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div role="status" aria-label="Wczytywanie zasobów">
        {[0, 1].map((i) => (
          <div key={i} className="flex h-11 items-center gap-3 border-b border-line">
            <Skeleton className="size-4 rounded" />
            <Skeleton className={cn('h-3.5', i ? 'w-1/4' : 'w-1/3')} />
            <span className="flex-1" />
            <Skeleton className="h-3 w-28" />
          </div>
        ))}
      </div>
    );
  } else if (query.isError) {
    body = (
      <ErrorState
        compact
        title="Nie udało się wczytać zasobów"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  } else if (query.data.length === 0) {
    body = (
      <EmptyState
        compact
        icon={Server}
        title="Nic tego klienta nie stoi u nas na serwerze"
        action={
          canAdd ? (
            <Button variant="ghost" size="s" icon={Plus} onClick={() => setAdding(true)} className={TOUCH_BUTTON}>
              Dodaj zasób
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    body = (
      <ul aria-label="Zasoby klienta">
        {query.data.map((a) => {
          const Icon = ASSET_TYPE_ICONS[a.type];
          const where = a.provider
            ? `${ASSET_TYPE_SUBTITLES[a.type].split(' ')[0]} · ${a.provider}`
            : ASSET_TYPE_SUBTITLES[a.type];
          const tone = a.renewal ? renewalTone(a.renewal) : 'neutral';
          return (
            <li key={a.id}>
              <Link
                to={`/utrzymanie/${a.id}`}
                className={cn(
                  'flex min-h-11 items-center gap-2.5 border-b border-line py-2 transition-colors hover:bg-surface',
                  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                )}
              >
                <Icon size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
                <span className="min-w-0 truncate text-body-s font-medium text-fg">{a.name}</span>
                {/* „gdzie” skraca się pierwsze (shrink 100), nazwa zasobu dopiero potem */}
                <span className="hidden min-w-0 shrink-[100] truncate text-caption text-fg-muted sm:inline">
                  {where}
                </span>
                <span className="flex-1" />
                {a.renewal && (
                  <span
                    className={cn(
                      'shrink-0 text-caption whitespace-nowrap',
                      tone === 'neutral' ? 'text-fg-secondary' : TONE_TEXT[tone],
                    )}
                  >
                    odnowienie {dateText(a.renewal.date)}
                  </span>
                )}
                {/* alarm z symulowanego monitora: krótka plakietka „Symulacja” (wąski wiersz profilu) zamiast czerwonego „Alarm” */}
                {a.status !== 'active' && (
                  <StatusPill
                    size="s"
                    tone={a.alarmSimulated ? 'warning' : ASSET_STATUS_TONES[a.status]}
                    className="shrink-0"
                  >
                    {a.alarmSimulated ? 'Symulacja' : ASSET_STATUS_LABELS[a.status]}
                  </StatusPill>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Section className={className} aria-label={title ?? 'Zasoby klienta'}>
      {title !== null && (
        <SectionHeader
          tone="strong"
          title={title}
          action={
            <Link
              to={`/utrzymanie?klient=${clientId}`}
              className={`rounded-xs text-body-s font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
            >
              Utrzymanie
            </Link>
          }
        />
      )}
      {body}
      <AddAssetDialog
        open={adding}
        onOpenChange={setAdding}
        defaultClientId={clientId}
        onSaved={(a) => navigate(`/utrzymanie/${a.id}`)}
      />
    </Section>
  );
}
